import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    Checkbox,
    CircularProgress,
    Grid,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mzc-pdc/ui";
import theme from "@styles/theme";
import { IconCheckLarge } from "@mzc-cloudplex/icons";
import formatDuration from "@cores/formatDuration";
import { PUBLIC_URL } from "@/constants";
import { alpha } from "@mui/material";
import { CircledIcon } from "@components_v2/icon";
import { useTranslation } from "react-i18next";
import { getCurrentLanguage } from "@lib/i18n";

const SelectMultiCaption = ({ data, onChange }) => {
    const { t } = useTranslation();
    const [selectedItems, setSelectedItems] = useState([]);

    const isChecked = useCallback(
        (elementId) => {
            return selectedItems.some((item) => item === elementId);
        },
        [selectedItems],
    );

    const onCheckElement = useCallback(
        (elementId) => {
            let selectedItems_ = [];
            const isChecked = selectedItems.some((item) => item === elementId);
            if (isChecked) selectedItems_ = selectedItems.filter((item) => item !== elementId);
            else selectedItems_ = [...selectedItems, elementId];

            setSelectedItems(selectedItems_);
            onChange(selectedItems_);
        },
        [selectedItems, onChange],
    );

    const onHandleClickCheckAll = useCallback(() => {
        if (selectedItems.length === data.elements?.length) {
            setSelectedItems([]);
            onChange([]);
        }
        if (selectedItems.length < data.elements?.length) {
            setSelectedItems(data.elements?.map((element) => element.id));
            onChange(data.elements?.map((element) => element.id));
        }
    }, [selectedItems, onChange]);

    const getLanguageName = (language) => {
        const lang = new Intl.DisplayNames([getCurrentLanguage()], { type: "language" });
        if (language) return lang.of(language);
        else return "";
    };

    useEffect(() => {
        if (!data) return;
        setSelectedItems(data.elements?.map((element) => element.id));
    }, []);

    return (
        <Grid container gap={1}>
            <Grid
                container
                flexDirection={"column"}
                sx={{
                    maxHeight: 320,
                    p: 2,
                    overflowY: "auto",
                    // DAM-646: 스크롤바 생길 때 콘텐츠 밀림 방지
                    scrollbarGutter: "stable",
                    backgroundColor: alpha(theme.palette.greySecondary[50], 0.5),
                    borderRadius: theme.spacing(1),
                    gap: "12px",
                }}
            >
                <Grid container gap={1} flexDirection={"row"} alignItems={"center"}>
                    <Checkbox
                        color={"greySecondary"}
                        checked={selectedItems.length > 0}
                        indeterminate={selectedItems.length > 0 && selectedItems.length < data?.elements?.length}
                        onChange={onHandleClickCheckAll}
                    />
                    <Typography variant={`body1_500`} component={"p"}>
                        {t(`common::label::Select all files`, `Select all files`)}
                        &nbsp;
                        <Typography color={theme.palette.primary.main} variant={`body1_500`}>
                            {selectedItems.length}
                        </Typography>
                        /{data.elements?.length}
                    </Typography>
                </Grid>

                <TableContainer
                    sx={{
                        flex: 1,
                        borderRadius: theme.spacing(1.5),
                        backgroundColor: theme.palette.common.white,
                        "&::-webkit-scrollbar-track": {
                            borderLeft: `1px solid ${theme.palette.greySecondary[100]}`,
                            borderRadius: "0 6px 6px 0",
                            backgroundColor: theme.palette.common.white,
                        },
                    }}
                >
                    <Table stickyHeader shadowHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t(`common::label::Label`)}</TableCell>
                                <TableCell>{t(`common::label::Language`)}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data?.elements?.map((element) => (
                                <TableRow
                                    key={element.id}
                                    selected={isChecked(element.id)}
                                    sx={{
                                        "&:hover": {
                                            backgroundColor: alpha(theme.palette.greySecondary["A100"], 0.48),
                                        },
                                        "Mui-selected": {
                                            backgroundColor: alpha(theme.palette.greySecondary["A100"], 0.36),
                                        },
                                    }}
                                >
                                    <TableCell>
                                        <Grid container flexDirection={"row"} gap={1}>
                                            <Grid
                                                item
                                                sx={{ cursor: "pointer" }}
                                                onClick={() => onCheckElement(element.id)}
                                            >
                                                <CircledIcon
                                                    size={20}
                                                    color={
                                                        isChecked(element.id)
                                                            ? theme.palette.primary.light
                                                            : theme.palette.greySecondary.light
                                                    }
                                                >
                                                    <IconCheckLarge
                                                        size={12}
                                                        htmlColor={theme.palette.primary.contrastText}
                                                    />
                                                </CircledIcon>
                                            </Grid>
                                            <Grid item>{element.label}</Grid>
                                        </Grid>
                                    </TableCell>
                                    <TableCell>{getLanguageName(element.language)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>
        </Grid>
    );
};

export default SelectMultiCaption;
